import { useContext } from 'react'
import { ScrollView, Text, View } from 'react-native'
import { useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { SELLER_CONFIRM_PAYMENT_PROOF } from '@/constants/graphql/mutations'
import type { BuyQueryResult, SellQueryResult } from '@/types/common'
import { fundsPinErrorMessage } from '@/utils/fundsPinError'
import { Banner, Button, useToast } from '@/components/base'
import BottomActionPanel from '@/components/BottomActionPanel'
import { CheckCircle } from '@/components/icon'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import { ChildOrderDetailSection } from '@/components/orders/child/DetailSection'
import { ChildOrderPaymentSection } from '@/components/orders/child/PaymentSection'
import RemainingTimeCountdown from '@/components/orders/child/RemainingTimeCountdown'
import { ImageViewerSection } from '@/components/orders/ImageViewerSection'
import { NotificationsContext } from '@/components/providers/notifications'
import { SecurityVerificationContext, SecurityVerificationTrigger } from '@/components/SecurityVerificationScreen'
import { useSuccessScreen } from '@/components/SuccessScreen'

export function ChildOrderWaitingForCoinReleaseScreen({
  data,
  type,
  parentInternalKey,
  parentId,
  requestAppeal,
  refetch
}: {
  data:
    | BuyQueryResult['userGetBuyOrderByInternalKey']
    | SellQueryResult['userGetSellOrderByInternalKey']['pairBuyOrder'][number]
  type: 'buy' | 'sell'
  parentInternalKey?: string
  parentId?: string
  requestAppeal: () => void
  refetch: () => void
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const { bottom } = useSafeAreaInsets()
  const {
    open: openVerificationModal,
    close: closeVerificationModal,
    setCb,
    useFormReturnValue: { setError }
  } = useContext(SecurityVerificationContext)
  const { open: openSuccessScreen, close: closeSuccessScreen } = useSuccessScreen()

  const loading = useLoadingIndicator()
  const { refresh } = useContext(NotificationsContext)
  const isVirtual = data.buyer === '0'

  const [sellerConfirmPaymentProof] = useMutation(SELLER_CONFIRM_PAYMENT_PROOF, {
    onCompleted: () => {
      loading.stop()
      openSuccessScreen({
        children: (
          <View className={'items-center gap-3 py-4'}>
            <CheckCircle className={'size-20 text-text-success'} />
            <View className={'items-center gap-2 pb-3'}>
              <Text className={'text-2xl font-semibold text-text-primary'}>{t('orders:transactionCompleted')}</Text>
              <Text className={'text-sm text-text-secondary'}>{t('orders:sellerReleasedToken')}</Text>
            </View>
          </View>
        ),
        onClose: () => {
          closeVerificationModal()
          closeSuccessScreen()
        }
      })
      refetch()
      refresh()
    },
    onError: (error) => {
      console.error('sellerConfirmPaymentProof error:', error)
      loading.stop()
      const { graphQLErrors } = error
      console.log(graphQLErrors)
      if (graphQLErrors.length === 0) {
        toast.show({ variant: 'error', description: t('auth:toast.error.timeoutError') })
        return
      }
      const pinErrorMessage = fundsPinErrorMessage(error)
      if (pinErrorMessage) {
        setError('withdrawalPassword', { message: pinErrorMessage })
        return
      }
      const errorCode = graphQLErrors[0]?.extensions?.code
      if (errorCode === 2000) {
        refetch()
        toast.show({ variant: 'error', description: t('auth:toast.error.invalidOperationHint') })
      }
    }
  })

  const handleConfirmPaymentProof = (withdrawalPassword: string) => {
    loading.start()

    sellerConfirmPaymentProof({
      variables: {
        sellOrderId: parentId,
        buyOrderId: data.id,
        withdrawalPassword
      }
    })
  }

  return (
    <View className={'flex-1 bg-background-secondary'}>
      <ScrollView className={'flex-1'} contentContainerStyle={{ paddingBottom: bottom + (type === 'sell' ? 72 : 0) }}>
        <View className={'gap-2 border-b border-border-10 bg-background-primary px-4 pb-[11px] pt-2'}>
          <Text className={'text-2xl font-semibold text-text-primary'}>
            {t(type === 'buy' ? 'orders:awaitingSellerRelease' : 'orders:awaitingRelease')}
          </Text>
          {type === 'sell' && (
            <View className={'flex-row gap-2'}>
              <Text className={'text-sm text-text-primary'}>{t('orders:releaseTimeRemaining')}</Text>
              <RemainingTimeCountdown time={data.paymentMadeAt} constraint={10} />
            </View>
          )}
          {type === 'buy' ? (
            <Text className={'font-sm text-text-secondary'}>
              {t(type === 'buy' ? 'orders:releaseAfterConfirmation' : 'orders:buyerPaidReleaseInstruction')}
            </Text>
          ) : (
            <View className={'py-1'}>
              <Banner>{t('orders:buyerPaidConfirmation')}</Banner>
            </View>
          )}
        </View>
        <ChildOrderPaymentSection data={data} type={type} />
        <ImageViewerSection images={data.paymentProofUrl} />
        <ChildOrderDetailSection data={data} type={type} parentInternalKey={parentInternalKey} />
      </ScrollView>
      {type === 'sell' && (
        <BottomActionPanel>
          <Button
            size={'md'}
            intent={'primary'}
            variant={'outline'}
            onPress={() => {
              requestAppeal()
            }}
            disabled={loading.isLoading}
          >
            {t(isVirtual ? 'orders:startAppeal' : 'orders:appealAmountMismatch')}
          </Button>
          <SecurityVerificationTrigger
            className={'flex-1'}
            onPress={() => {
              setCb(() => handleConfirmPaymentProof)
              openVerificationModal()
            }}
            disabled={loading.isLoading}
          >
            {t('orders:confirmReceiptAndRelease')}
          </SecurityVerificationTrigger>
        </BottomActionPanel>
      )}
    </View>
  )
}
